import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { CheckCircle, Circle, ArrowRight, ArrowLeft, Save, ClipboardCheck } from "lucide-react";
import OrganicShape from "../Board/OrganicShape";
import { submitGovernancePulseAssessment } from "../../services/businessDashApi";
import { GOVERNANCE_PULSE_QUESTIONS } from "../../lib/governancePulseQuestions";

/**
 * Full-screen flow for Governance Pulse. POST body matches chemistry dimensions
 * (decision_making_score, collaboration_score, sustainability_score, communication_score).
 * Optional pulse_request_id links a chair-approved cycle (numeric or pulse-req-NNN).
 */
export default function GovernancePulseAssessment({
  isOpen,
  onClose,
  onComplete,
  onSave,
  pulseRequestId,
  notes: notesProp,
}) {
  const questions = GOVERNANCE_PULSE_QUESTIONS;
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [savedProgress, setSavedProgress] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const defaults = {};
    questions.forEach((q) => {
      if (q.defaultAnswer) defaults[q.id] = q.defaultAnswer;
    });
    setAnswers(defaults);
    setCurrentQuestion(0);
    setShowResults(false);
    setSubmitError(null);
  }, [isOpen, questions]);

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const question = questions[currentQuestion];
  const canProceed = question && answers[question.id];

  const handleAnswer = (questionId, answerId) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answerId }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      void finishAssessment();
    } else {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestion((prev) => Math.max(0, prev - 1));
  };

  const handleSave = () => {
    setSavedProgress(true);
    onSave?.({ currentQuestion, answers, timestamp: new Date().toISOString() });
    setTimeout(() => setSavedProgress(false), 2000);
  };

  const finishAssessment = async () => {
    const categoryScores = {
      collaboration: 0,
      "decision-making": 0,
      sustainability: 0,
      communication: 0,
    };
    const categoryCounts = {
      collaboration: 0,
      "decision-making": 0,
      sustainability: 0,
      communication: 0,
    };
    const answersArray = [];
    let totalScore = 0;

    questions.forEach((q) => {
      const selected = answers[q.id];
      if (!selected) return;
      const option = q.options.find((o) => o.id === selected);
      if (!option) return;
      const cat = option.category;
      categoryScores[cat] += option.value;
      categoryCounts[cat] += 1;
      totalScore += option.value;
      answersArray.push({
        question_id: q.id,
        selected_option: selected,
        score: option.value,
        category: cat,
      });
    });

    if (answersArray.length !== questions.length) {
      setSubmitError("Please answer all questions.");
      return;
    }

    const payload = {
      answers: answersArray,
      total_score: totalScore,
      decision_making_score: categoryScores["decision-making"] || 0,
      collaboration_score: categoryScores.collaboration || 0,
      sustainability_score: categoryScores.sustainability || 0,
      communication_score: categoryScores.communication || 0,
    };

    const notes = typeof notesProp === "string" && notesProp.trim() ? notesProp.trim() : undefined;
    if (notes) payload.notes = notes;

    if (pulseRequestId != null && pulseRequestId !== "") {
      const raw = String(pulseRequestId).trim();
      if (/^pulse-req-/i.test(raw)) {
        payload.pulse_request_id = raw;
      } else {
        const n = Number(raw);
        payload.pulse_request_id = Number.isFinite(n) ? n : raw;
      }
    }

    try {
      await submitGovernancePulseAssessment(payload);
      setSubmitError(null);
      const results = {
        completedAt: new Date().toISOString(),
        totalScore,
        categoryScores,
        categoryCounts,
        answers: answersArray,
      };
      onComplete?.(results);
      setShowResults(true);
    } catch (e) {
      console.error("Governance Pulse submit failed:", e);
      setSubmitError(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          "Could not save your responses. Try again or contact support."
      );
    }
  };

  const handleCloseResults = () => {
    setShowResults(false);
    onClose?.();
  };

  if (showResults) {
    return (
      <Dialog open={isOpen} onOpenChange={handleCloseResults}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Pulse complete</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-2 text-center">
            <CheckCircle className="w-16 h-16 text-primary mx-auto" />
            <p className="text-foreground/80">Your Governance Pulse responses have been saved.</p>
            <OrganicShape className="p-6 bg-gradient-subtle">
              <p className="text-sm text-muted-foreground">Thank you for contributing to the board record.</p>
            </OrganicShape>
            <Button
              onClick={handleCloseResults}
              className="w-full !bg-[rgba(9,54,107,0.72)] !text-white hover:!bg-[rgba(9,54,107,0.88)]"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!question) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" />
            Governance Pulse
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {submitError && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">{submitError}</p>
          )}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Badge variant="muted" className="px-3 py-1">
                Question {currentQuestion + 1} of {questions.length}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={handleSave}
                className={savedProgress ? "text-primary" : ""}
              >
                <Save className="w-4 h-4 mr-1" />
                {savedProgress ? "Saved!" : "Save Progress"}
              </Button>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <OrganicShape className="p-6 bg-gradient-subtle">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{question.title}</h3>
                <p className="text-foreground/80 leading-relaxed">{question.scenario}</p>
              </div>
              <div className="space-y-3">
                {question.options.map((option) => {
                  const isSelected = answers[question.id] === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? "border-primary bg-primary/10 shadow-md"
                          : "border-border hover:border-primary/50 hover:bg-primary/5"
                      }`}
                      onClick={() => handleAnswer(question.id, option.id)}
                    >
                      <div className="flex items-start gap-3">
                        {isSelected ? (
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                        ) : (
                          <Circle className="w-5 h-5 text-foreground/40 mt-0.5 shrink-0" />
                        )}
                        <span className={`text-sm ${isSelected ? "text-foreground font-medium" : "text-foreground/80"}`}>
                          {option.text}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </OrganicShape>

          <div className="flex justify-between items-center">
            <Button variant="outline" type="button" onClick={handlePrevious} disabled={currentQuestion === 0}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              disabled={!canProceed}
              className="!bg-[rgba(9,54,107,0.72)] !text-white hover:!bg-[rgba(9,54,107,0.88)]"
            >
              {isLastQuestion ? "Submit Pulse" : "Next"}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
